import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, formatDate, statusTone } from "@/lib/format";
import { StatusBadge } from "@/lib/statusColor";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import MultiSelectFilter from "@/components/app/MultiSelectFilter";
import { X } from "lucide-react";

const fmtInvDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—");

const PAYMENT_STATUSES = ["Pending", "Partially Paid", "Paid", "Cancelled"];

export default function Payments() {
  const { user } = useAuth();
  const isManager = ["Manager", "SuperAdmin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ freelancer_id: "", project_id: "", amount: 0, status: "Pending", amount_paid: 0 });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/payments"); setItems(data);
    const p = await api.get("/projects"); setProjects(p.data);
    if (isManager) { const f = await api.get("/freelancers"); setFreelancers(f.data); }
  };
  useEffect(() => { load(); }, []);

  const monthOptions = Array.from(new Map(items.filter((i) => i.invoice_month_key).map((i) => [i.invoice_month_key, i.invoice_month])))
    .sort((a, b) => b[0].localeCompare(a[0])).map(([, label]) => label);
  const shown = items.filter((i) =>
    (!statusFilter || i.status === statusFilter) &&
    (!monthFilter.length || monthFilter.includes(i.invoice_month)))
    .sort((a, b) => (b.invoice_date || "").localeCompare(a.invoice_date || ""));
  const fname = (fid) => freelancers.find((x) => x.id === fid)?.name || (user.linked_freelancer_id === fid ? user.name : "—");
  const pname = (pid) => projects.find((x) => x.id === pid)?.name || "—";

  const markPaid = async (p) => {
    try { await api.patch(`/payments/${p.id}/mark-paid`); load(); toast.success("Marked paid"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (p) => setEditing({ ...p, amount_paid: p.amount_paid || 0 });

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/payments/${editing.id}`, { status: editing.status, amount_paid: Number(editing.amount_paid || 0) });
      setEditing(null); load(); toast.success("Updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount), amount_paid: Number(form.amount_paid || 0), project_id: form.project_id || null });
      setNewOpen(false); setForm({ freelancer_id: "", project_id: "", amount: 0, status: "Pending", amount_paid: 0 });
      load(); toast.success("Invoice created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportCsv = () => {
    const rows = [["Invoice", "Freelancer", "Project", "Amount", "Amount Paid", "Status", "Payment Date",
      "Project ID", "Project Name", "Work Order ID", "Work Order", "Invoice Number", "Invoice Date", "Invoice Month",
      "Client Details", "Invoice Amount", "Payment Amount", "Payment Status"]];
    for (const p of shown) rows.push([p.invoice_number, fname(p.freelancer_id), pname(p.project_id), p.amount, p.amount_paid || 0, p.status,
      p.payment_date ? fmtInvDate(p.payment_date) : "",
      p.project_number || "", p.project_name || "", p.work_order_id || "", p.work_order_title || "", p.invoice_number,
      fmtInvDate(p.invoice_date), p.invoice_month || "", p.client_name || "", p.amount, p.amount_paid || 0, p.status]);
    const csv = "\uFEFF" + rows.map((r) => r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const suffix = monthFilter.length ? `-${monthFilter.map((m) => m.replace(" ", "")).join("_")}` : "";
    const a = document.createElement("a"); a.href = url; a.download = `payments${suffix}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const statusColor = (s) => {
    switch (s) {
      case "Paid": return "#00E054";
      case "Pending": return "#FFEA00";
      case "Partially Paid": return "#D4FF00";
      case "Cancelled": return "#FF2A00";
      default: return "#E5E5E5";
    }
  };

  const downloadInvoice = (p) => {
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${p.invoice_number}</title>
    <style>
      body{font-family:'IBM Plex Sans',system-ui,sans-serif;padding:48px;color:#0A0A0A;background:#FAFAFA}
      h1{font-family:'Space Grotesk',sans-serif;letter-spacing:-.02em;margin:0;font-size:44px}
      .meta{margin-top:24px;font-family:'IBM Plex Mono',monospace;font-size:12px;color:#525252}
      .row{display:flex;justify-content:space-between;border-bottom:1px solid #E5E5E5;padding:10px 0;font-size:14px}
      .box{border:2px solid #0A0A0A;padding:24px;margin-top:32px;background:#fff}
      .label{font-family:'IBM Plex Mono',monospace;text-transform:uppercase;letter-spacing:.14em;font-size:11px;color:#525252}
      .amt{font-family:'IBM Plex Mono',monospace;font-size:44px;font-weight:600;margin-top:6px}
      .status{display:inline-block;padding:6px 12px;background:${statusColor(p.status)};color:#0A0A0A;font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:.14em;text-transform:uppercase;border:1px solid #0A0A0A;font-weight:600}
    </style></head><body>
      <div style="border-bottom:2px solid #0A0A0A;padding-bottom:16px">
        <div class="label">Invoice</div>
        <h1>${p.invoice_number}</h1>
      </div>
      <div class="meta">
        <div class="row"><span>Freelancer</span><span>${fname(p.freelancer_id)}</span></div>
        <div class="row"><span>Project</span><span>${pname(p.project_id)}</span></div>
        <div class="row"><span>Issued</span><span>${fmtInvDate(p.invoice_date)}</span></div>
        <div class="row"><span>Payment Date</span><span>${p.payment_date ? formatDate(p.payment_date) : "—"}</span></div>
      </div>
      <div class="box">
        <div class="label">Amount</div>
        <div class="amt">₹ ${Number(p.amount).toLocaleString("en-IN")}</div>
        ${p.status === "Partially Paid" ? `<div class="label" style="margin-top:16px">Received</div><div class="amt" style="font-size:24px">₹ ${Number(p.amount_paid || 0).toLocaleString("en-IN")}</div>` : ""}
        <div style="margin-top:24px" class="label">Status</div>
        <div style="margin-top:6px"><span class="status">${p.status}</span></div>
      </div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        description={isManager ? "Track invoices, payouts and pending balances." : "Your invoices and payouts."}
        actions={
          <>
            {isManager && <button data-testid="new-invoice-btn" onClick={() => setNewOpen(true)} className="border-2 border-black bg-white px-4 py-2 text-sm hard-shadow-hover">+ New Invoice</button>}
            <button data-testid="export-csv-btn" onClick={exportCsv} className="bg-black text-white px-4 py-2 text-sm hard-shadow-hover">Export CSV</button>
          </>
        }
      />
      <div className="p-8">
        <div className="mb-4 flex gap-3 flex-wrap items-center">
          <select data-testid="pay-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-black rounded-none px-3 py-2 text-sm bg-white">
            <option value="">All Statuses</option>
            {PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <MultiSelectFilter testid="pay-month-filter" label="Invoice Month" options={monthOptions} selected={monthFilter} onChange={setMonthFilter} />
          {monthFilter.map((m) => (
            <span key={m} data-testid={`pay-month-chip-${m.replace(" ", "-")}`} className="inline-flex items-center gap-1 bg-royal-800 text-cream text-xs px-2.5 py-1 rounded-full">
              {m}<button data-testid={`pay-month-chip-remove-${m.replace(" ", "-")}`} onClick={() => setMonthFilter(monthFilter.filter((x) => x !== m))} aria-label={`Remove ${m}`}><X size={12} /></button>
            </span>
          ))}
          {monthFilter.length > 0 && <button data-testid="pay-month-clear-all" onClick={() => setMonthFilter([])} className="text-xs underline underline-offset-4">Clear months</button>}
          <div className="ml-auto text-xs text-neutral-500 font-mono-tight" data-testid="payments-count">{shown.length} / {items.length}</div>
        </div>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="fms-table w-full" data-testid="payments-table">
            <thead><tr><th>Invoice</th>{isManager && <th>Freelancer</th>}<th>Project</th><th>Amount</th><th>Paid</th><th>Status</th><th>Invoice Date</th><th>Invoice Month</th><th></th></tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                  <td className="font-mono-tight">{p.invoice_number}</td>
                  {isManager && <td className="text-sm">{fname(p.freelancer_id)}</td>}
                  <td className="text-sm" data-testid={`payment-project-${p.id}`}>
                    {p.work_order_title && <div className="text-neutral-800">{p.work_order_title}</div>}
                    {!p.work_order_title && !p.project_number && <div className="text-neutral-500">—</div>}
                    {p.project_number && <div className="font-mono-tight text-xs text-neutral-500">{p.project_number}</div>}
                  </td>
                  <td className="font-mono-tight">{formatINR(p.amount)}</td>
                  <td className="font-mono-tight">{formatINR(p.amount_paid || 0)}</td>
                  <td><StatusBadge status={p.status} testid={`status-badge-${p.id}`} /></td>
                  <td className="font-mono-tight text-neutral-500 whitespace-nowrap" data-testid={`invoice-date-${p.id}`}>{fmtInvDate(p.invoice_date)}</td>
                  <td className="text-sm whitespace-nowrap" data-testid={`invoice-month-${p.id}`}>{p.invoice_month || "—"}</td>
                  <td className="whitespace-nowrap">
                    {isManager && <button data-testid={`edit-payment-${p.id}`} onClick={() => openEdit(p)} className="text-xs underline underline-offset-4 mr-3">Edit</button>}
                    {isManager && p.status !== "Paid" && <button data-testid={`mark-paid-${p.id}`} onClick={() => markPaid(p)} className="text-xs underline underline-offset-4 mr-3">Mark Paid</button>}
                    <button data-testid={`download-invoice-${p.id}`} onClick={() => downloadInvoice(p)} className="text-xs underline underline-offset-4">Invoice</button>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={isManager ? 9 : 8} className="text-center text-neutral-500 py-8">No payments.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none border-2 border-black">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Invoice</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><label className="label-caps block mb-1">Freelancer</label>
              <select required value={form.freelancer_id} onChange={(e) => setForm({ ...form, freelancer_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">Select freelancer</option>
                {freelancers.filter((f) => f.status === "active").map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div><label className="label-caps block mb-1">Project</label>
              <select required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                <option value="">Select project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-caps block mb-1">Amount (INR)</label><Input required type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-black" /></div>
              <div><label className="label-caps block mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                  {PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {form.status === "Partially Paid" && (
              <div><label className="label-caps block mb-1">Amount Paid So Far (INR)</label><Input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} className="rounded-none border-black" /></div>
            )}
            <DialogFooter><button data-testid="submit-invoice" type="submit" className="bg-black text-white px-4 py-2 text-sm">Create</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none border-2 border-black">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Edit Invoice</DialogTitle></DialogHeader>
              <form onSubmit={saveEdit} className="space-y-3">
                <div className="text-xs text-neutral-500 font-mono-tight">{editing.invoice_number}</div>
                <div><label className="label-caps block mb-1">Status</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full border border-black rounded-none px-3 py-2 text-sm bg-white">
                    {PAYMENT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                {editing.status === "Partially Paid" && (
                  <div><label className="label-caps block mb-1">Amount Received (INR)</label><Input type="number" value={editing.amount_paid} onChange={(e) => setEditing({ ...editing, amount_paid: e.target.value })} className="rounded-none border-black" /></div>
                )}
                <DialogFooter><button type="submit" data-testid="save-payment" className="bg-black text-white px-4 py-2 text-sm">Save</button></DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
